import { useState, useMemo } from 'react';
import { clubEvents, EVENT_TYPES, EVENT_TYPE_COLORS, MONTH_NAMES, type EventType } from '../../../mocks/events';

const TYPE_ICONS: Record<string, string> = {
  理監事會議: 'ri-government-line',
  月例會: 'ri-mic-line',
  聯誼會: 'ri-cup-line',
  全國社活動: 'ri-flag-2-line',
  子社團活動: 'ri-group-line',
};

const STATS = [
  {
    icon: 'ri-calendar-event-line',
    value: clubEvents.length,
    unit: '場',
    label: '全年活動場次',
  },
  {
    icon: 'ri-apps-2-line',
    value: new Set(clubEvents.map((e) => e.type)).size,
    unit: '種',
    label: '活動類別',
  },
  {
    icon: 'ri-map-2-line',
    value: new Set(clubEvents.map((e) => e.month)).size,
    unit: '個月',
    label: '全年涵蓋',
  },
  {
    icon: 'ri-user-star-line',
    value: clubEvents.filter((e) => e.director).length,
    unit: '位',
    label: '輪值理事',
  },
];

export default function EventFilterSection() {
  const [selectedType, setSelectedType] = useState<EventType | '全部'>('全部');
  const [selectedMonth, setSelectedMonth] = useState<number | 0>(0);

  const filteredEvents = useMemo(() => {
    return clubEvents.filter((e) => {
      const typeMatch = selectedType === '全部' || e.type === selectedType;
      const monthMatch = selectedMonth === 0 || e.month === selectedMonth;
      return typeMatch && monthMatch;
    });
  }, [selectedType, selectedMonth]);

  const groupedByMonth = useMemo(() => {
    const map: Record<number, typeof filteredEvents> = {};
    filteredEvents.forEach((e) => {
      if (!map[e.month]) map[e.month] = [];
      map[e.month].push(e);
    });
    return Object.entries(map)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([month, events]) => ({ month: Number(month), events }));
  }, [filteredEvents]);

  return (
    <section className="py-16 sm:py-24 bg-stone-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">

        {/* ── Stats Banner ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-14">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="bg-white rounded-2xl border border-gray-200 px-6 py-7 flex flex-col items-center text-center"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-50 mb-4">
                <i className={`${stat.icon} text-2xl text-orange-600`}></i>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-gray-900 leading-none">{stat.value}</span>
                <span className="text-base font-semibold text-orange-600">{stat.unit}</span>
              </div>
              <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Section Title */}
        <div className="mb-12 text-center">
          <p className="text-sm font-medium text-orange-600 tracking-widest uppercase mb-3">2026 Activity Schedule</p>
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">年度活動總覽</h2>
          <p className="text-lg text-gray-500">選擇類別或月份，快速找到您感興趣的活動</p>
        </div>

        {/* Filters */}
        <div className="mb-10 space-y-6">

          {/* Type Filter */}
          <div>
            <p className="text-lg font-bold text-gray-800 mb-4">
              <span className="inline-block w-1.5 h-5 bg-orange-500 rounded-full mr-2 align-middle"></span>
              活動類別
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* 全部 */}
              <button
                onClick={() => setSelectedType('全部')}
                className={`flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border-2 transition-all duration-150 cursor-pointer ${
                  selectedType === '全部'
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'
                }`}
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-apps-2-line text-2xl"></i>
                </div>
                <span className="text-base font-bold whitespace-nowrap">全部</span>
              </button>

              {/* 各類別 */}
              {(() => {
                const TYPE_SOLID: Record<string, { active: string; idle: string; icon: string }> = {
                  理監事會議: { active: 'bg-rose-600 border-rose-600 text-white', idle: 'bg-rose-50 border-rose-300 text-rose-700', icon: TYPE_ICONS['理監事會議'] },
                  月例會:     { active: 'bg-amber-500 border-amber-500 text-white', idle: 'bg-amber-50 border-amber-300 text-amber-700', icon: TYPE_ICONS['月例會'] },
                  聯誼會:     { active: 'bg-teal-600 border-teal-600 text-white', idle: 'bg-teal-50 border-teal-300 text-teal-700', icon: TYPE_ICONS['聯誼會'] },
                  全國社活動: { active: 'bg-orange-600 border-orange-600 text-white', idle: 'bg-orange-50 border-orange-300 text-orange-700', icon: TYPE_ICONS['全國社活動'] },
                  子社團活動: { active: 'bg-stone-600 border-stone-600 text-white', idle: 'bg-stone-100 border-stone-400 text-stone-700', icon: TYPE_ICONS['子社團活動'] },
                };
                return EVENT_TYPES.map((type) => {
                  const cfg = TYPE_SOLID[type];
                  const isActive = selectedType === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      className={`flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border-2 transition-all duration-150 cursor-pointer ${
                        isActive ? cfg.active : cfg.idle
                      }`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`${cfg.icon} text-2xl`}></i>
                      </div>
                      <span className="text-base font-bold whitespace-nowrap">{type}</span>
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {/* Month Filter */}
          <div>
            <p className="text-lg font-bold text-gray-800 mb-4">
              <span className="inline-block w-1.5 h-5 bg-orange-500 rounded-full mr-2 align-middle"></span>
              選擇月份
            </p>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
              {/* 全年 */}
              <button
                onClick={() => setSelectedMonth(0)}
                className={`flex flex-col items-center justify-center py-4 rounded-2xl border-2 transition-all duration-150 cursor-pointer ${
                  selectedMonth === 0
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'
                }`}
              >
                <span className="text-xl font-black leading-none">全</span>
                <span className="text-sm font-semibold mt-1">全年</span>
              </button>

              {/* 各月份 */}
              {Object.entries(MONTH_NAMES).map(([m, eng]) => (
                <button
                  key={m}
                  onClick={() => setSelectedMonth(Number(m))}
                  className={`flex flex-col items-center justify-center py-4 rounded-2xl border-2 transition-all duration-150 cursor-pointer ${
                    selectedMonth === Number(m)
                      ? 'bg-orange-600 border-orange-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-orange-400 hover:bg-orange-50'
                  }`}
                >
                  <span className="text-2xl font-black leading-none">{m}</span>
                  <span className="text-xs font-semibold mt-1 opacity-70">{eng}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Result Count */}
        <div className="text-lg text-gray-500 mb-8 text-center">
          共找到 <span className="font-black text-gray-900 text-2xl">{filteredEvents.length}</span> 項活動
        </div>

        {/* Events List */}
        {groupedByMonth.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5">
              <i className="ri-calendar-close-line text-6xl"></i>
            </div>
            <p className="text-xl">此篩選條件下無活動</p>
          </div>
        ) : (
          <div className="space-y-12">
            {groupedByMonth.map(({ month, events }) => (
              <div key={month}>
                <div className="flex items-center gap-5 mb-6">
                  <div className="bg-gray-900 text-white rounded-2xl px-7 py-4 flex items-baseline gap-2 flex-shrink-0">
                    <span className="text-4xl font-black">{String(month).padStart(2, '0')}</span>
                    <span className="text-base font-medium text-white/60">{MONTH_NAMES[month]}</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="text-lg font-semibold text-gray-500 flex-shrink-0">{events.length} 場活動</span>
                </div>

                <div className="space-y-4">
                  {events.map((event) => {
                    const colors = EVENT_TYPE_COLORS[event.type];
                    return (
                      <div
                        key={event.id}
                        className={`bg-white rounded-2xl border overflow-hidden hover:border-orange-300 transition-colors duration-200 cursor-pointer ${event.director ? 'border-amber-200' : 'border-gray-200'}`}
                      >
                        {/* 輪值理事橫幅 — 月例會專屬 */}
                        {event.director && (
                          <div className="bg-amber-400 px-6 py-2.5 flex items-center gap-2">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-user-star-fill text-sm text-amber-900"></i>
                            </div>
                            <span className="text-sm font-bold text-amber-900 tracking-wide">輪值理事</span>
                            <span className="text-base font-black text-amber-900 ml-1">{event.director}</span>
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row sm:items-stretch">
                          <div className="sm:w-36 bg-gray-50 border-b sm:border-b-0 sm:border-r border-gray-200 flex flex-row sm:flex-col items-center justify-center gap-3 sm:gap-1 px-5 py-5 sm:py-7 flex-shrink-0">
                            <span className="text-3xl sm:text-4xl font-black text-orange-600 leading-none">
                              {event.date.split('（')[0]}
                            </span>
                            {event.date.includes('（') && (
                              <span className="text-sm font-semibold text-gray-500">
                                {event.date.match(/（([^）]+)）/)?.[1]}
                              </span>
                            )}
                          </div>

                          <div className="flex-1 px-7 py-6">
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-base font-bold border-2 ${colors.bg} ${colors.text} ${colors.border}`}>
                                <div className="w-5 h-5 flex items-center justify-center">
                                  <i className={`${TYPE_ICONS[event.type]} text-base`}></i>
                                </div>
                                {event.type}
                              </span>
                            </div>

                            <h3 className="text-2xl font-black text-gray-900 mb-3 leading-snug">
                              {event.title}
                            </h3>

                            {event.description && (
                              <p className="text-lg text-gray-500 leading-relaxed mb-4">
                                {event.description}
                              </p>
                            )}

                            {event.location && (
                              <div className="flex items-center gap-2 text-base text-gray-500">
                                <div className="w-5 h-5 flex items-center justify-center">
                                  <i className="ri-map-pin-line text-base"></i>
                                </div>
                                <span>{event.location}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
